import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  Button,
  Calendar,
  message,
  Tooltip,
  Select,
  Dropdown,
} from 'antd';
import {
  PlusOutlined,
  LeftOutlined,
  RightOutlined,
  DownOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { eventService, Event } from '../services/events';
import { taskService, taskTimelogService, Task, TaskReview, TaskStatusLog, TaskTimelog } from '../services/tasks';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { customerService, Customer } from '../services/customers';
import { businessService, Business } from '../services/businesses';
import { staffService, Staff } from '../services/staffs';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { getDeadlineSettings } from '../utils/deadlineSettings';
import '../styles/calendar-custom.css';

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

type CalendarView = 'month' | 'week' | 'day';

type CalendarItem = {
  id: number | string;
  customer_name: string;
  location?: string;
  type: Event['type'] | 'deadline';
  all_day: boolean;
  start: string;
  end: string;
  alert: boolean;
  attachment?: string;
  note?: string;
  task_date_label?: string;
  staff_id?: number | null;
  staff?: Event['staff'] | null;
  task?: Task;
};

export const CalendarPage: React.FC = () => {
  const [events, setEvents] = useState<CalendarItem[]>([]);
  const [timelogs, setTimelogs] = useState<TaskTimelog[]>([]);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [staffs, setStaffs] = useState<Staff[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [staffsLoading, setStaffsLoading] = useState(false);
  const [selectedStaffIds, setSelectedStaffIds] = useState<number[]>([]);
  const [selectAllStaff, setSelectAllStaff] = useState(true);
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
  const [view, setView] = useState<CalendarView>('month');
  const [expandedDates, setExpandedDates] = useState<Record<string, boolean>>({});
  const [eventFilters, setEventFilters] = useState<Record<string, boolean>>({
    appointment: true,
    task: true,
    leave: true,
    deadline: true,
  });
  const navigate = useNavigate();
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();
  const { user } = useAuth();
  const canViewCalendar = hasPermission(user, 'calendar.view');
  const canViewPersonal = hasPermission(user, 'customer.personal.view');
  const canViewStaff = hasPermission(user, 'settings.view');
  const canViewBusiness = hasPermission(user, 'customer.business.view');
  const canCreate = hasPermission(user, 'calendar.create');
  const canViewTaskDetails = hasPermission(user, 'projects.tasks.details.view');
  const isOwnerOrAdmin = Boolean(user?.roles?.some((role) => ['owner', 'admin', 'developer'].includes(role)));
  const canManageStaffSelection = canViewStaff && isOwnerOrAdmin;

  useEffect(() => {
    if (!canViewPersonal) {
      setCustomers([]);
      return;
    }
    loadCustomers();
  }, [canViewPersonal]);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Calendar
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  useEffect(() => {
    if (!canManageStaffSelection) {
      setStaffs([]);
      return;
    }
    loadStaffs();
  }, [canManageStaffSelection]);

  useEffect(() => {
    if (!canViewBusiness) {
      setBusinesses([]);
      return;
    }
    loadBusinesses();
  }, [canViewBusiness]);

  useEffect(() => {
    if (!canViewCalendar) {
      setEvents([]);
      setTimelogs([]);
      return;
    }
    loadEvents();
  }, [currentDate, view, selectedStaffIds, selectAllStaff, canViewCalendar, customers, businesses]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    if (!canManageStaffSelection) {
      setSelectAllStaff(false);
      setSelectedStaffIds([user.id]);
    } else if (selectedStaffIds.length === 0) {
      setSelectAllStaff(true);
    }
  }, [user, canManageStaffSelection, selectedStaffIds.length]);

  const loadCustomers = async () => {
    if (!canViewPersonal) {
      setCustomers([]);
      return;
    }
    try {
      const response = await customerService.getAll();
      setCustomers(response.data || []);
    } catch {
      message.error('Failed to load customers');
    }
  };

  const loadStaffs = async () => {
    setStaffsLoading(true);
    try {
      const response = await staffService.getAll();
      setStaffs(Array.isArray(response) ? response : []);
    } catch {
      message.error('Failed to load staffs');
    } finally {
      setStaffsLoading(false);
    }
  };

  const loadBusinesses = async () => {
    if (!canViewBusiness) {
      setBusinesses([]);
      return;
    }
    try {
      const response = await businessService.getAll();
      setBusinesses(response.data || []);
    } catch {
      message.error('Failed to load businesses');
    } finally {
      // no-op
    }
  };

  const loadEvents = async () => {
    if (!canViewCalendar) {
      setEvents([]);
      setTimelogs([]);
      return;
    }
    setLoading(true);
    try {
      const [eventsResponse, timelogsResponse, tasksResponse] = await Promise.all([
        eventService.getAll(),
        taskTimelogService.getAll().catch(() => ({ data: [] })),
        taskService.getAll().catch(() => ({ data: [] })),
      ]);
      const eventsData = (eventsResponse.data || []).filter((event: Event) => event.type !== 'task');
      const taskEvents = buildTaskEvents(tasksResponse.data || []);
      const deadlineEvents = buildDeadlineEvents(customers, businesses);
      let combinedEvents: CalendarItem[] = [...eventsData, ...taskEvents, ...deadlineEvents];
      
      if (!selectAllStaff) {
        const allowedStaffIds = (selectedStaffIds.length > 0
          ? selectedStaffIds
          : (!canManageStaffSelection && user?.id ? [user.id] : []));
        const allowedSet = new Set(allowedStaffIds.map((id) => Number(id)));
        combinedEvents = combinedEvents.filter((event: CalendarItem) => {
          const staffId = event.staff_id ?? event.staff?.id ?? event.task?.staff_id;
          if (staffId === null || staffId === undefined) return false;
          const staffIdNumber = Number(staffId);
          if (Number.isNaN(staffIdNumber)) return false;
          return allowedSet.has(staffIdNumber);
        });
      }
      
      setEvents(combinedEvents);
      setTimelogs(timelogsResponse.data || []);
    } catch {
      message.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date: Dayjs) => {
    setCurrentDate(date);
  };

  const handlePrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, view === 'month' ? 'month' : view === 'week' ? 'week' : 'day'));
  };

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, view === 'month' ? 'month' : view === 'week' ? 'week' : 'day'));
  };

  const handleFilterChange = (type: string, checked: boolean) => {
    setEventFilters(prev => ({ ...prev, [type]: checked }));
  };

  const filteredEvents = events.filter(event => eventFilters[event.type] !== false);

  const getListData = (value: Dayjs) => {
    return filteredEvents.filter(event => {
      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);
      const valueDate = value.format('YYYY-MM-DD');
      const startDate = eventStart.format('YYYY-MM-DD');
      const endDate = eventEnd.format('YYYY-MM-DD');
      return valueDate >= startDate && valueDate <= endDate;
    });
  };

  const calculateDailyTimeCost = (value: Dayjs): number => {
    const valueDate = value.format('YYYY-MM-DD');
    let totalHours = 0;

    // Calculate time from events (non-all-day events)
    filteredEvents.forEach(event => {
      if (event.type === 'task') {
        return;
      }
      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);
      const startDate = eventStart.format('YYYY-MM-DD');
      const endDate = eventEnd.format('YYYY-MM-DD');
      
      if (valueDate >= startDate && valueDate <= endDate) {
        if (event.all_day) {
          // All-day events count as 8 hours
          totalHours += 8;
        } else {
          // Calculate actual hours for this specific day
          if (valueDate === startDate && valueDate === endDate) {
            // Same day event
            const hours = eventEnd.diff(eventStart, 'hour', true);
            totalHours += hours;
          } else if (valueDate === startDate) {
            // First day of multi-day event
            const endOfDay = eventStart.endOf('day');
            const hours = endOfDay.diff(eventStart, 'hour', true);
            totalHours += hours;
          } else if (valueDate === endDate) {
            // Last day of multi-day event
            const startOfDay = eventEnd.startOf('day');
            const hours = eventEnd.diff(startOfDay, 'hour', true);
            totalHours += hours;
          } else {
            // Middle day of multi-day event (full day)
            totalHours += 24;
          }
        }
      }
    });

    // Add time from task timelogs
    timelogs.forEach(timelog => {
      const timelogDate = dayjs(timelog.date).format('YYYY-MM-DD');
      if (timelogDate === valueDate && timelog.enabled) {
        totalHours += Number(timelog.hour) || 0;
      }
    });

    return totalHours;
  };

  const calculateDailyTaskHours = (value: Dayjs): number => {
    const valueDate = value.format('YYYY-MM-DD');
    let total = 0;
    const countedTasks = new Set<string>();
    filteredEvents.forEach((event) => {
      if (!event.task || event.type !== 'task') {
        return;
      }
      const eventStart = dayjs(event.start).format('YYYY-MM-DD');
      const eventEnd = dayjs(event.end).format('YYYY-MM-DD');
      if (valueDate < eventStart || valueDate > eventEnd) {
        return;
      }
      const key = `${event.task.id}-${valueDate}`;
      if (countedTasks.has(key)) {
        return;
      }
      countedTasks.add(key);
      total += Number(event.task.actual_hour) || 0;
    });

    return total;
  };

  const buildTaskEvents = (tasks: Task[]): CalendarItem[] => {
    return tasks.flatMap((task) => {
      const entries = getTaskEventDates(task).filter((entry) => Boolean(entry.value));
      return entries.map((entry) => {
        const dateValue = dayjs(entry.value as string);
        const start = dateValue.startOf('day').format('YYYY-MM-DD HH:mm:ss');
        const end = dateValue.endOf('day').format('YYYY-MM-DD HH:mm:ss');
        return {
          id: `task-${task.id}-${entry.key}`,
          customer_name: getTaskDisplayName(task),
          type: 'task',
          all_day: true,
          start,
          end,
          alert: false,
          task_date_label: entry.label,
          staff_id: task.staff_id ?? null,
          staff: task.staff ? { id: task.staff.id, name: task.staff.name } : null,
          task,
        };
      });
    });
  };

  const buildDeadlineEvents = (customersData: Customer[], businessesData: Business[]): CalendarItem[] => {
    const settings = getDeadlineSettings();
    const referenceYear = currentDate.year();
    const deadlineEvents: CalendarItem[] = [];

    if (canViewPersonal) {
      customersData.forEach((customer) => {
        const engagementTokens = (customer.engagement || '')
          .split(',')
          .map((token) => token.trim().toUpperCase())
          .filter(Boolean);
        if (engagementTokens.includes('B')) {
          deadlineEvents.push(
            buildPersonalDeadlineEvent(customer, 'B', settings.personal.b.month, settings.personal.b.day, referenceYear),
          );
        }
        if (engagementTokens.includes('BE')) {
          deadlineEvents.push(
            buildPersonalDeadlineEvent(customer, 'BE', settings.personal.be.month, settings.personal.be.day, referenceYear),
          );
        }
      });
    }

    if (canViewBusiness) {
      businessesData.forEach((business) => {
        const yearEndDate = business.year_end ? dayjs(business.year_end) : null;
        if (yearEndDate) {
          const yearEnd = yearEndDate.year(referenceYear);
          deadlineEvents.push(
            buildBusinessDeadlineEvent(business, 'Account', yearEnd.add(settings.business.accountOffsetMonths, 'month')),
          );
          deadlineEvents.push(
            buildBusinessDeadlineEvent(business, 'Audit', yearEnd.add(settings.business.auditOffsetMonths, 'month')),
          );
          deadlineEvents.push(
            buildBusinessDeadlineEvent(business, 'Tax', yearEnd.add(settings.business.taxOffsetMonths, 'month')),
          );
        }
        if (business.incorporation_date) {
          const incorporation = dayjs(business.incorporation_date).year(referenceYear);
          deadlineEvents.push(
            buildBusinessDeadlineEvent(
              business,
              'Annual Return',
              incorporation.add(settings.business.annualReturnOffsetYears, 'year'),
            ),
          );
        }
      });
    }

    return deadlineEvents.filter(Boolean);
  };

  const buildPersonalDeadlineEvent = (
    customer: Customer,
    label: string,
    month: number,
    day: number,
    year: number,
  ): CalendarItem => {
    const dueDate = dayjs().year(year).month(month - 1).date(day);
    return {
      id: `deadline-personal-${customer.id}-${label}-${year}`,
      customer_name: customer.name,
      type: 'deadline',
      all_day: true,
      start: dueDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end: dueDate.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      alert: false,
      note: `Personal ${label} deadline`,
      staff_id: customer.staff_id ?? null,
    };
  };

  const buildBusinessDeadlineEvent = (business: Business, label: string, dueDate: Dayjs): CalendarItem => {
    const entityLabel = business.entity ? ` (${business.entity})` : '';
    return {
      id: `deadline-business-${business.id}-${label}-${dueDate.format('YYYY')}`,
      customer_name: business.name,
      type: 'deadline',
      all_day: true,
      start: dueDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end: dueDate.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      alert: false,
      note: `Business ${label} deadline${entityLabel}`,
      staff_id: business.staff_id ?? null,
    };
  };

  const getTaskDisplayName = (task: Task): string => {
    if (task.type === 'business') {
      return task.business?.name || '';
    }
    if (task.type === 'personal') {
      return task.customer?.name || '';
    }
    return task.business?.name || task.customer?.name || '';
  };

  const getEventDisplayLabel = (event: CalendarItem): string => {
    if (event.type === 'leave') {
      const baseName = event.staff?.name || event.customer_name;
      if (!event.all_day) {
        const timeRange = `${dayjs(event.start).format('HH:mm')}-${dayjs(event.end).format('HH:mm')}`;
        return `${timeRange} ${baseName}`.trim();
      }
      return baseName;
    }
    if (!event.all_day && event.type === 'appointment') {
      const timeRange = `${dayjs(event.start).format('HH:mm')}-${dayjs(event.end).format('HH:mm')}`;
      return `${timeRange} ${event.customer_name}`.trim();
    }
    if (event.task && event.task.actual_hour) {
      return `${event.task.actual_hour}h ${event.customer_name}`;
    }
    return event.customer_name;
  };

  const getTaskEventDates = (task: Task): { key: string; label: string; value?: string | null }[] => {
    return [
      { key: 'created', label: 'Created', value: task.created_date || task.created_at },
      { key: 'plan', label: 'Plan', value: task.plan_date },
      { key: 'due', label: 'Due', value: task.due_date },
    ];
  };

  const formatTaskDate = (value?: string | null) => {
    return value ? dayjs(value).format('D MMM YYYY') : '-';
  };

  const getTaskStatusLogs = (task: Task): TaskStatusLog[] => {
    return task.statusLogs || task.status_logs || [];
  };

  const getLatestReviewDate = (reviews: TaskReview[] | undefined, predicate: (review: TaskReview) => boolean) => {
    if (!reviews || reviews.length === 0) return undefined;
    const filteredReviews = reviews.filter(predicate);
    if (filteredReviews.length === 0) return undefined;
    return filteredReviews.reduce((latest, review) => {
      if (!latest) {
        return review.created_at;
      }
      return dayjs(review.created_at).isAfter(dayjs(latest)) ? review.created_at : latest;
    }, filteredReviews[0].created_at as string);
  };

  const getLatestStatusDate = (task: Task, status: string): string | undefined => {
    const logs = getTaskStatusLogs(task).filter((log) => log.to_status === status);
    if (logs.length === 0) return undefined;
    return logs.reduce((latest, log) => {
      if (!latest) {
        return log.created_at;
      }
      return dayjs(log.created_at).isAfter(dayjs(latest)) ? log.created_at : latest;
    }, logs[0].created_at as string);
  };

  const dateFullCellRender = (value: Dayjs) => {
    const listData = getListData(value);
    const taskHours = calculateDailyTaskHours(value);
    const isCurrentMonth = value.month() === currentDate.month();
    const dateKey = value.format('YYYY-MM-DD');
    const isExpanded = !!expandedDates[dateKey];
    const visibleEvents = isExpanded ? listData : listData.slice(0, 3);
    const remainingCount = listData.length - visibleEvents.length;
    
    return (
      <div className={`calendar-month-cell ${isCurrentMonth ? '' : 'is-outside'}`}>
        <div className="calendar-month-cell-header">
          <span className="calendar-month-date">{value.date()}</span>
          {taskHours > 0 && <span className="calendar-month-hour">{taskHours.toFixed(1)}h</span>}
        </div>
        <div className="calendar-month-events">
          {visibleEvents.map((event) => (
            <Tooltip key={event.id} title={renderEventTooltip(event)} placement="bottom" overlayStyle={{ maxWidth: 300 }}>
              <div
                className={`calendar-chip calendar-chip--${event.type}`}
                onClick={canViewCalendar ? () => handleEventClick(event) : undefined}
              >
                {getEventDisplayLabel(event)}
              </div>
            </Tooltip>
          ))}
          {remainingCount > 0 && !isExpanded && (
            <button
              type="button"
              className="calendar-more-button"
              onClick={() => {
                setExpandedDates((prev) => ({ ...prev, [dateKey]: true }));
              }}
            >
              ... +{remainingCount}
            </button>
          )}
          {listData.length > 3 && isExpanded && (
            <button
              type="button"
              className="calendar-more-button"
              onClick={() => {
                setExpandedDates((prev) => ({ ...prev, [dateKey]: false }));
              }}
            >
              Collapse
            </button>
          )}
        </div>
      </div>
    );
  };

  const handleEventClick = (event: CalendarItem) => {
    if (!canViewCalendar) {
      return;
    }
    if (event.type === 'task') {
      if (event.task?.id && canViewTaskDetails) {
        navigate(`/tasks/${event.task.id}/edit`);
      }
      return;
    }
    if (event.type === 'deadline') {
      return;
    }
    navigate(`/calendar/events/${event.id}/edit`);
  };

  const handleAddEvent = () => {
    navigate('/calendar/events/new');
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'appointment':
        return { bg: '#e6f7ff', dot: '#1890ff' };
      case 'task':
        return { bg: '#fff7e6', dot: '#fa8c16' };
      case 'leave':
        return { bg: '#f6ffed', dot: '#52c41a' };
      case 'deadline':
        return { bg: '#fff1f0', dot: '#ff4d4f' };
      default:
        return { bg: '#f5f5f5', dot: '#8c8c8c' };
    }
  };

  const renderEventTooltip = (event: CalendarItem) => {
    if (event.task) {
      const task = event.task;
      const createdDate = task.created_date || task.created_at;
      const planDate = task.plan_date;
      const reviewDate = getLatestStatusDate(task, 'Review')
        ?? getLatestReviewDate(task.reviews, (review) => !review.customer_id);
      const confirmDate = getLatestStatusDate(task, 'Approve')
        ?? getLatestReviewDate(task.reviews, (review) => Boolean(review.customer_id));
      const completedDate = getLatestStatusDate(task, 'Completed')
        ?? ((task.status === 'Completed' || task.is_completed) ? task.updated_at : undefined);
      return (
        <div style={{ maxWidth: 260 }}>
          <div style={{ fontWeight: 500, marginBottom: 6, fontSize: '14px' }}>{event.customer_name}</div>
          {event.task_date_label && (
            <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
              Date: {event.task_date_label}
            </div>
          )}
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            Created: {formatTaskDate(createdDate)}
          </div>
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            Plan: {formatTaskDate(planDate)}
          </div>
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            Review: {formatTaskDate(reviewDate)}
          </div>
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            Confirm: {formatTaskDate(confirmDate)}
          </div>
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            Complete: {formatTaskDate(completedDate)}
          </div>
        </div>
      );
    }
    return (
      <div style={{ maxWidth: 250 }}>
        <div style={{ fontWeight: 500, marginBottom: 6, fontSize: '14px' }}>
          {event.type === 'leave' ? (event.staff?.name || event.customer_name) : event.customer_name}
        </div>
        {event.location && (
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            📍 {event.location}
          </div>
        )}
        <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
          {event.all_day 
            ? 'All Day Event' 
            : `${dayjs(event.start).format('D MMM YYYY HH:mm')} - ${dayjs(event.end).format('D MMM YYYY HH:mm')}`
          }
        </div>
        <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
          Type: <span style={{ textTransform: 'capitalize' }}>{event.type}</span>
        </div>
        {event.staff?.name && (
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            Staff: {event.staff.name}
          </div>
        )}
        {event.alert && (
          <div style={{ fontSize: '12px', marginBottom: 4, color: '#fff' }}>
            🔔 Alert Enabled
          </div>
        )}
        {event.note && (
          <div style={{ 
            fontSize: '12px', 
            marginTop: 6, 
            borderTop: '1px solid rgba(255,255,255,0.3)', 
            paddingTop: 6,
            color: '#fff'
          }}>
            {event.note}
          </div>
        )}
      </div>
    );
  };

  const getMonthLabel = () => {
    if (view === 'month') {
      return currentDate.format('MMMM YYYY');
    } else if (view === 'week') {
      const weekStart = currentDate.startOf('week');
      const weekEnd = currentDate.endOf('week');
      return `${weekStart.format('D MMM YYYY')} - ${weekEnd.format('D MMM YYYY')}`;
    } else {
      return currentDate.format('D MMM YYYY');
    }
  };

  const getWeekDays = () => {
    const weekStart = currentDate.startOf('week');
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(weekStart.clone().add(i, 'day'));
    }
    return days;
  };

  const getEventsForDate = (date: Dayjs) => {
    return filteredEvents.filter(event => {
      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);
      const dateStr = date.format('YYYY-MM-DD');
      const startDate = eventStart.format('YYYY-MM-DD');
      const endDate = eventEnd.format('YYYY-MM-DD');
      return dateStr >= startDate && dateStr <= endDate;
    });
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays();
    
    return (
      <div className="calendar-week">
        <div className="calendar-week-header">
          {weekDays.map((day) => {
            const isToday = day.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');
            return (
              <div
                key={day.format('YYYY-MM-DD')}
                className={`calendar-week-header-cell ${isToday ? 'is-today' : ''}`}
                onClick={() => setCurrentDate(day)}
              >
                <div className="calendar-week-day">{day.format('ddd').toUpperCase()}</div>
                <div className="calendar-week-date">{day.format('DD')}</div>
              </div>
            );
          })}
        </div>
        <div className="calendar-week-body">
          {weekDays.map((day) => {
            const dayEvents = getEventsForDate(day);
            return (
              <div key={day.format('YYYY-MM-DD')} className="calendar-week-cell">
                {dayEvents.map((event) => (
                  <Tooltip key={event.id} title={renderEventTooltip(event)} placement="bottom" overlayStyle={{ maxWidth: 300 }}>
                    <div
                      className={`calendar-chip calendar-chip--${event.type}`}
                      onClick={() => handleEventClick(event)}
                    >
                      {getEventDisplayLabel(event)}
                    </div>
                  </Tooltip>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayEvents = getEventsForDate(currentDate);
    const totalHours = calculateDailyTimeCost(currentDate);
    
    return (
      <div className="calendar-day">
        <div className="calendar-day-header">
          <div className="calendar-day-title">
            <span className="calendar-day-label">{currentDate.format('ddd').toUpperCase()}</span>
            <span className="calendar-day-date">{currentDate.format('DD')}</span>
            {totalHours > 0 && <span className="calendar-day-hours">{totalHours.toFixed(1)}h</span>}
          </div>
          <div className="calendar-day-count">{dayEvents.length} events</div>
        </div>
        <div className="calendar-day-list">
          {dayEvents.map((event) => (
            <Tooltip key={event.id} title={renderEventTooltip(event)} placement="right" overlayStyle={{ maxWidth: 300 }}>
              <div
                className={`calendar-day-card calendar-day-card--${event.type}`}
                onClick={() => handleEventClick(event)}
              >
                <div className="calendar-day-card-meta">
                  <span className="calendar-day-card-type">{event.type.toUpperCase()}</span>
                  <span className="calendar-day-card-time">
                    {event.all_day ? 'All Day' : `${dayjs(event.start).format('HH:mm')} - ${dayjs(event.end).format('HH:mm')}`}
                  </span>
                </div>
                <div className="calendar-day-card-title">{event.customer_name}</div>
                {event.location && <div className="calendar-day-card-sub">📍 {event.location}</div>}
                {event.note && <div className="calendar-day-card-sub">{event.note}</div>}
              </div>
            </Tooltip>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="calendar-shell">
        <div className="calendar-topbar">
          <div className="calendar-topbar-left">
            {canManageStaffSelection ? (
              <Select<(number | '__all__')[]>
                className="calendar-pill"
                mode="multiple"
                maxTagCount={1}
                value={selectAllStaff ? ['__all__'] : selectedStaffIds}
                placeholder="All Staff"
                loading={staffsLoading}
                onChange={(values) => {
                  const hasAll = values.includes('__all__');
                  if (values.length === 0 || (hasAll && values.length === 1)) {
                    setSelectAllStaff(true);
                    setSelectedStaffIds([]);
                    return;
                  }
                  const next = values.filter((value): value is number => value !== '__all__');
                  setSelectAllStaff(false);
                  setSelectedStaffIds(next);
                }}
                options={[
                  { label: 'All Staff', value: '__all__' },
                  ...staffs.map((staff) => ({ label: staff.name, value: staff.id })),
                ]}
              />
            ) : (
              <Button className="calendar-pill" disabled>
                {user?.name || 'My Calendar'}
              </Button>
            )}
            <Dropdown
              trigger={['click']}
              dropdownRender={() => (
                <div className="calendar-dropdown">
                  <div className="calendar-dropdown-title">Event</div>
                  {(['appointment', 'task', 'leave', 'deadline'] as const).map((type) => (
                    <label key={type} className="calendar-dropdown-item">
                      <Checkbox
                        checked={eventFilters[type]}
                        onChange={(e) => handleFilterChange(type, e.target.checked)}
                      />
                      <span className={`calendar-dot calendar-dot--${type}`} />
                      <span className="calendar-dropdown-label">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                    </label>
                  ))}
                </div>
              )}
            >
              <Button className="calendar-pill">Event <DownOutlined /></Button>
            </Dropdown>
          </div>

          <div className="calendar-topbar-center">
            <Button className="calendar-icon-btn" icon={<LeftOutlined />} onClick={handlePrevMonth} />
            <div className="calendar-month-label">{getMonthLabel()}</div>
            <Button className="calendar-icon-btn" icon={<RightOutlined />} onClick={handleNextMonth} />
          </div>

          <div className="calendar-topbar-right">
            <div className="calendar-view-toggle">
              <Button className={view === 'month' ? 'is-active' : ''} onClick={() => setView('month')}>
                M
              </Button>
              <Button className={view === 'week' ? 'is-active' : ''} onClick={() => setView('week')}>
                W
              </Button>
              <Button className={view === 'day' ? 'is-active' : ''} onClick={() => setView('day')}>
                D
              </Button>
            </div>
            {canCreate && (
              <Button className="calendar-add-btn" icon={<PlusOutlined />} onClick={handleAddEvent}>
                Add
              </Button>
            )}
          </div>
        </div>

        <div className="calendar-body">
          {view === 'month' && (
            <div className="calendar-month">
              <Calendar
                value={currentDate}
                onChange={handleDateChange}
                dateFullCellRender={dateFullCellRender}
                headerRender={() => null}
                mode="month"
              />
            </div>
          )}
          {view === 'week' && renderWeekView()}
          {view === 'day' && renderDayView()}
        </div>
      </div>
    </div>
  );
};
